import { blue, grey } from "@ant-design/colors"
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Card, Col, Divider, Dropdown, Row, Typography } from "antd"
import { useContext, useState } from "react"
import { Workarea } from "../../../../../domain/job"
import { WorkareaGroupWithWorkareas } from "../../../../../domain/workareas/group"
import { sum } from "../../../../../helpers/array"
import VSpace from "../../../../common/VSpace"
import { centsToDollarsDisplay } from "../../../../displayFormatters"
import { ItemsContext } from "../../../../Items/ItemsRoot"
import ApprovedOrOnHoldDisplay from "../../List/ApprovedOrOnHoldDisplay"
import DeleteWorkareaGroupModal from "../../List/DeleteWorkareaGroupModal"
import PricingTypeDisplay from "../../../PricingTypeDisplay"
import TruncatedName from "../../../../common/TruncatedName"

const { Text } = Typography

type Props = {
  group: WorkareaGroupWithWorkareas
  groupWorkareas: Workarea[]
  selectedWorkareaId: number | undefined
  selectedGroupId: string | undefined
  onWorkareaSelection: (workareaId: number) => void
  onGroupSelection: (groupId: string) => void
  cardOptions: React.ReactNode
}

const WorkareaGroupCard = ({ group, groupWorkareas, selectedWorkareaId, selectedGroupId, onWorkareaSelection, onGroupSelection, cardOptions }: Props) => {
  const { workareaSummaries } = useContext(ItemsContext)
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false)

  const totalPrice = sum(groupWorkareas, (w: Workarea) => {
    const summary = workareaSummaries.find(s => s.workareaId === w.id)
    return summary?.finalPrice || 0
  })

  const estimatedHours = sum(groupWorkareas, (w: Workarea) => {
    const summary = workareaSummaries.find(s => s.workareaId === w.id)
    return summary?.estimatedHours || 0
  })

  const GroupOptions = <Dropdown
    trigger={['click']}
    menu={{
      items: [
        {
          key: 'delete',
          label: 'Delete',
          onClick: () => setIsDeleteGroupModalOpen(true),
        },
      ]
    }}
  >
    <Button type="text" icon={<FontAwesomeIcon icon={faEllipsisV} />} />
  </Dropdown>

  return <div style={{ backgroundColor: '#f5f5f5', borderRadius: 8, border: `1px solid rgba(0, 0, 0, 0.15)` }}>
    <Card
      style={{
        borderRadius: '8px 8px 0 0',
        cursor: 'pointer',
        backgroundColor: selectedGroupId === group.id ? blue[0] : '#f5f5f5',
        borderWidth: selectedGroupId === group.id ? '1px' : '0 0 1px 0',
        borderColor: selectedGroupId === group.id ? blue.primary : 'rgba(0, 0, 0, 0.15)'
      }}
      onClick={() => onGroupSelection(group.id)}
    >
      <Row gutter={20}>
        <Col span={20}>
          <VSpace style={{ width: '100%' }}>
            <Text strong>
              <TruncatedName name={group.name} />
            </Text>
            <div>
              <Text strong>{centsToDollarsDisplay(totalPrice)}</Text>
              <Divider type='vertical' style={{ borderColor: grey.primary }} />
              <Text type='secondary'>{estimatedHours} Hours</Text>
            </div>
            <PricingTypeDisplay pricingType={group.pricingType} />
            <ApprovedOrOnHoldDisplay workareaGroup={group} />
          </VSpace>
        </Col>
        <Col span={4} style={{ alignContent: 'center' }}>
          {GroupOptions}
        </Col>
      </Row>
    </Card>

    <VSpace style={{ padding: 10 }}>
      {
        groupWorkareas.map((workarea) => {
          const workareaSummary = workareaSummaries.find(s => s.workareaId === workarea.id)
          return <Card
            key={workarea.id}
            style={{
              border: selectedWorkareaId === workarea.id ? `1px solid ${blue.primary}` : '1px solid #f0f0f0',
              backgroundColor: selectedWorkareaId === workarea.id ? blue[0] : '#fff',
              cursor: 'pointer',
              borderRadius: 8,
              paddingLeft: 16
            }}
            onClick={() => onWorkareaSelection(workarea.id)}
          >
            <Row gutter={20}>
              <Col span={20}>
                <VSpace style={{ width: '100%' }}>
                  <TruncatedName name={workarea.name} />
                  <div>
                    <Text>{centsToDollarsDisplay(workareaSummary?.finalPrice ?? 0)}</Text>
                    <Divider type='vertical' style={{ borderColor: grey.primary }} />
                    <Text type='secondary'>{workareaSummary?.estimatedHours || 0} Hours</Text>
                  </div>
                </VSpace>
              </Col>
              <Col span={4} style={{ alignContent: 'center' }}>
                {cardOptions}
              </Col>
            </Row>
          </Card>
        })
      }
    </VSpace>

    <DeleteWorkareaGroupModal 
      isOpen={isDeleteGroupModalOpen}
      jobId={group.jobId}
      onCancel={() => setIsDeleteGroupModalOpen(false)}
      workareaCount={groupWorkareas.length}
      workareaGroupId={group.id}
      workareaGroupName={group.name}
    />
  </div>
}

export default WorkareaGroupCard
